import styles from './product-skus-table.module.scss'
import { observer } from 'mobx-react-lite'
import {
  TableContainer,
  TableHeader,
  TableBodyContainer,
  TableBodyLine,
  TableBodyLineProps,
  PaginationButtons,
  Input,
  Checkbox,
  AttributeGroupDTO,
  ImageSquare,
  SelectOption,
  AttributeGroupValueDTO
} from '@touche-lts/panel-ui/dist'
import { ProductSKUAttributes, ProductSKUsDTO } from 'models/product-skus'
import { useRootStore } from 'mobx-stores/store-provider'
import { ProductSKUTableSortProps } from 'mobx-stores/utils'
import { TableHorizontalScrollControls } from 'components'
import { useRef, useState } from 'react'

const ProductsSKUsTableComponent = (props) => { // TODO-A To make model here

  const {
    productId
  } = props,
  {
    productsSKUsStore: {
      productSKUsAttributeStore,
      setColumnAttributeSelected,
      setAllChecked,
      allCheckedProducts
    },
    productsSKUTableStore: {
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      searchProps,
      onSearchPropsChange,
      filterTableSkus,
      tableSkus,
      paginationInfo,
      onPageSelect,
      setTableSKUCheckboxState,
      editProductSku,
      editProductPrice,
      editProductStock,
      updateTableSkusOnBlur,
      setSearchProps,
      setTableSkusAllCheckboxState
    }
  } = useRootStore(),

  tableBodyContainerRef = useRef<HTMLDivElement>(null),
  [tableHeaderMarginLeft, setTableHeaderMarginLeft] = useState<number>(0),
  [tableBodyScrollWidth, setTableBodyScrollWidth] = useState<number>(undefined),

  handleCheckboxAction = (isChecked: boolean, productIndex: number) => {
    setTableSKUCheckboxState(isChecked, productIndex);
  },

  handleAllChecked = (): void => {
    setAllChecked(!allCheckedProducts)
    setTableSkusAllCheckboxState()
  },

  dynamicSelectByAttributes = (attributeValues: AttributeGroupValueDTO[]): SelectOption<any>[] => {
    return attributeValues.map(attrValue => ({
      label: attrValue.name,
      value: attrValue.id
    })) 
  }

  const getBodyLineCells = (
    product: ProductSKUsDTO,
    productIndex: number
  ): TableBodyLineProps['cells'] => {
    const cells: TableBodyLineProps['cells'] = [
      {
        widthPx: 75,
        children: (
          <>
            {productIndex === 0 && (
              <Checkbox 
                className={styles.selectAllCheckbox} 
                isChecked={product.isChecked} 
                onClick={handleAllChecked} 
              />
            )}
            <Checkbox isChecked={product.isChecked} onClick={() => handleCheckboxAction(product.isChecked, productIndex)} />
          </>
        ),
      },
      {
        widthPx: 250,
        children: (
          <>
            {!!product.images.length && (
              <ImageSquare src={product.images[0]?.url} width={30} className={styles.skuImage} />
            )} 
            <Input 
              colorPreset='dark'
              value={product.sku}
              onBlur={() => updateTableSkusOnBlur(productId)}
              onChange={sku => editProductSku(sku, productIndex)}
            />
          </>
        ),
        isColumnMarked: isColumnMarked(ProductSKUTableSortProps.SKU)
      },
      {
        widthPx: 230,
        children: (
          <span>{!!product?.price ? product?.price : '-'}</span>
        ),
        isColumnMarked: isColumnMarked(ProductSKUTableSortProps.PRICE)
      },
      {
        widthPx: 230,
        children: (
          <span>{!!product?.stock ? product?.stock : '-'}</span>
        ),
        isColumnMarked: isColumnMarked(ProductSKUTableSortProps.STOCK)
      },
      {
        widthPx: 230,
        children: (
          <span>{!!product?.revenie ? product?.revenie : '-'}</span>
        ),
        isColumnMarked: isColumnMarked(ProductSKUTableSortProps.SOLD)
      },
      {
        widthPx: 230,
        children: (
          <span>{!!product?.revenie ? product?.revenie : '-'}</span>
        ),
        isColumnMarked: isColumnMarked(ProductSKUTableSortProps.REVENUE)
      }
    ]

    let tableCellsBodyLineAttributes = [];

    productSKUsAttributeStore.forEach((productAttribute: AttributeGroupDTO) => {
      tableCellsBodyLineAttributes.push(
        {
          widthPx: 120,
          children: (
            <>
              {product.attributes.map((skuAttribute: ProductSKUAttributes) => {
                if(productAttribute.id === skuAttribute.groupId) {
                  return (
                    <span className={styles.attributesText}>{skuAttribute.name}</span>
                  )
                } else {
                  return (
                    <p></p>
                  )
                }
              })
              }
            </>
          )
        }
      )
    })

    cells.splice(2, 0, ...tableCellsBodyLineAttributes); // set dynamic attribute body line cells

    return cells
  },

  tableColumns: any = [
    {
      label: '',
      widthPx: 75
    },
    {
      label: 'SKU(STOCK KEEPING UNIT)',
      widthPx: 250,
      isColumnMarked: isColumnMarked(ProductSKUTableSortProps.SKU),
      sortDirection: getSortDirection(ProductSKUTableSortProps.SKU),
      setSortDirection: handleSortDirectionChange(ProductSKUTableSortProps.SKU),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.name,
        onChange: (name => {
          onSearchPropsChange({ name })
          setTimeout(() => {
            filterTableSkus()
          },1200)
        })
      }
    },
    {
      label: 'PRICE',
      widthPx: 230,
      isColumnMarked: isColumnMarked(ProductSKUTableSortProps.PRICE),
      sortDirection: getSortDirection(ProductSKUTableSortProps.PRICE),
      setSortDirection: handleSortDirectionChange(
        ProductSKUTableSortProps.PRICE
      ),
      sliderFilterProps: {
        label: 'price for product',
        min: 0,
        max: 100,
        value: [searchProps.minPrice, searchProps.maxPrice],
        onChange: ([minPrice, maxPrice]) => {
          onSearchPropsChange({ minPrice, maxPrice })
          setTimeout(() => {
            filterTableSkus()
          },1200)
        }
      }
    },
    {
      label: 'STOCK',
      widthPx: 230,
      isColumnMarked: isColumnMarked(ProductSKUTableSortProps.STOCK),
      sortDirection: getSortDirection(ProductSKUTableSortProps.STOCK),
      setSortDirection: handleSortDirectionChange(
        ProductSKUTableSortProps.STOCK
      ),
      sliderFilterProps: {
        label: 'price for product',
        min: 0,
        max: 10000,
        value: [searchProps.minStock, searchProps.maxStock],
        onChange: ([minStock, maxStock]) => {
          onSearchPropsChange({ minStock, maxStock })
          setTimeout(() => {
            filterTableSkus()
          },1200)
        }
      }
    },
    {
      label: 'SOLD',
      widthPx: 230,
      isColumnMarked: isColumnMarked(ProductSKUTableSortProps.SOLD),
      sortDirection: getSortDirection(ProductSKUTableSortProps.SOLD),
      setSortDirection: handleSortDirectionChange(
        ProductSKUTableSortProps.SOLD
      ),
      sliderFilterProps: {
        label: 'sold products price',
        min: 0,
        max: 10000,
        value: [searchProps.minSold, searchProps.maxSold],
        onChange: ([minSold, maxSold]) => {
          onSearchPropsChange({ minSold, maxSold })
          setTimeout(() => {
            filterTableSkus()
          },1200)
        }
      }
    },
    {
      label: 'REVENUE',
      widthPx: 230,
      isColumnMarked: isColumnMarked(ProductSKUTableSortProps.REVENUE),
      sortDirection: getSortDirection(ProductSKUTableSortProps.REVENUE),
      setSortDirection: handleSortDirectionChange(
        ProductSKUTableSortProps.REVENUE
      ),
      sliderFilterProps: {
        label: 'revenue of each sku',
        min: 0,
        max: 10000,
        value: [searchProps.minRevenue, searchProps.maxRevenue],
        onChange: ([minRevenue, maxRevenue]) =>
          onSearchPropsChange({ minRevenue, maxRevenue })
      }
    },
  ]

  tableColumns.splice(2, 0, ...productSKUsAttributeStore.map((productAttribute: AttributeGroupDTO, indexAtrribute: number) => {
    const attrKey = 'attr' + indexAtrribute;
    const tableWidth = 655 + productSKUsAttributeStore.length * 120;
    const widthBase = 100 / tableWidth;
    console.log('productSKUsAttributeStore.length: ', 655 + productSKUsAttributeStore.length * 120);
    console.log('widthBase: ', widthBase);
    if(indexAtrribute === 0) {
      return {
        label: productAttribute.name,
        widthPx: 120,
        // widthPercentage: ((100/(655 + (productSKUsAttributeStore.length * 120)))*(productSKUsAttributeStore.length * 120))/productSKUsAttributeStore.length,
        // widthPercentage: 100/(5 + productSKUsAttributeStore.length),
        // preset: 'grouped',
        // groupLabel: 'Attributes',
        // groupWidthPercentage: productSKUsAttributeStore.length * (100/(5 + productSKUsAttributeStore.length)) + 0.1,
        // groupWidthPercentage: productSKUsAttributeStore.length * Math.ceil((100 /(6 + productSKUsAttributeStore.length))),
        // style: {
        //   borderTop: '7px solid #4c4c4c'
        // },
        selectProps: {
          options: dynamicSelectByAttributes(productAttribute.values),
          value: searchProps[attrKey],
          // style: {
          //   marginTop: '-2px'
          // },
          onChange: (attrKey) => {
            setColumnAttributeSelected(attrKey, indexAtrribute)
            filterTableSkus()
            setSearchProps({attrKey})
          }
          // onChange: (status: OrdersTableStatus) => onSearchPropsChange({ status })
        }
      }
    }
    return {
      label: productAttribute.name,
      // preset: 'grouped',
      widthPx: 120,
      // groupWidthPercentage: (100/(655 + (productSKUsAttributeStore.length * 120)))*(productSKUsAttributeStore.length * 120) + 5,
      // style: {
      //   borderTop: '7px solid #4c4c4c',
      // },
      selectProps: {
        options: dynamicSelectByAttributes(productAttribute.values),
        value: searchProps[attrKey],
        // style: {
        //   marginTop: '-2px'
        // },
        onChange: (attrKey) => {
          setColumnAttributeSelected(attrKey, indexAtrribute)
          filterTableSkus()
          setSearchProps({attrKey})
        }
      }
    }
  })); // set dynamic attribute header line cells

  return (
    <TableContainer className={styles.tableSkus} colorPreset='dark'>
      <TableHeader 
        colorPreset='dark' 
        tableColumns={tableColumns} 
        style={{
          marginLeft: tableHeaderMarginLeft,
          width: tableBodyScrollWidth,
          zIndex: 1
        }}
      />

      <TableHorizontalScrollControls
        containerRef={tableBodyContainerRef}
        onScrollLeft={(pixels) => setTableHeaderMarginLeft(-pixels)}
        onScrollWidthChange={(scrollWidth) => setTableBodyScrollWidth(scrollWidth)}
      />

      <TableBodyContainer 
        className={styles.tableBodyContainer} 
        ref={tableBodyContainerRef}
      >
        {tableSkus && tableSkus.map((product: ProductSKUsDTO, key: number) => {
          return (
            <TableBodyLine colorPreset='dark' key={product.id} cells={getBodyLineCells(product, key)} />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons
        colorPreset='dark'
        alwaysShow
        {...paginationInfo}
        onPageSelect={onPageSelect}
      />
    </TableContainer>
  )
},

ProductsSKUsTableProduct = observer(ProductsSKUsTableComponent)
export { ProductsSKUsTableProduct }
